import type { PlayerState } from './types';

type Selector<T> = (state: PlayerState) => T;
type Listener<T> = (newVal: T, oldVal: T) => void;

interface Subscription {
  selector: Selector<unknown>;
  listener: Listener<unknown>;
}

/**
 * Single source of truth for VOD determination.
 * - manifest/metadata says VOD → VOD
 * - manifest/metadata says live → live (even with finite DVR duration)
 * - unknown → not VOD (no timeline until an engine determines the type;
 *   NativeEngine writes streamType directly when metadata is observable)
 *
 * duration is intentionally not consulted: duration alone cannot distinguish
 * a live DVR stream (finite duration) from a VOD, so only streamType decides.
 */
export function deriveIsVod(streamType: 'live' | 'vod' | 'unknown'): boolean {
  return streamType === 'vod';
}

/** State fields callers may write — isVod is derived-only and read-only. */
export type SettablePlayerState = Omit<PlayerState, 'isVod'>;

const DEFAULT_STATE: PlayerState = {
  isPlaying: false,
  isOnline: true,
  streamType: 'unknown',
  isVod: false,
  showPoster: false,
  isMuted: false,
  isFullscreen: false,
  isPiP: false,
  isTheatreMode: false,
  clipsAvailable: false,
  downloadAvailable: false,
  volume: 0.3,
  quality: 'auto',
  availableQualities: [],
  viewers: 0,
  currentTime: 0,
  duration: 0,
  bufferLength: 0,
  latency: 0,
  bandwidth: 0,
  streamKey: '',
  cdnServer: null,
  engineName: '',
  isLowLatency: false,
  latencyMode: 'normal',
  llhlsAvailable: false,
  windowIsActive: true,
  loading: false,
  startupHiding: false,
  isSwitching: false,
  accessDenied: false,
  tokenRecoveryPending: false,
  showAdultWarning: false,
  announce: null,
  user: null,
  isDebugVisible: false,
};

export class Store {
  private state: PlayerState;
  private subs: Subscription[] = [];

  constructor(initial?: Partial<SettablePlayerState>) {
    this.state = { ...DEFAULT_STATE, ...initial, isVod: this.derive({ ...DEFAULT_STATE, ...initial }) };
  }

  getState(): PlayerState {
    return this.state;
  }

  setState(partial: Partial<SettablePlayerState>): void {
    const prev = this.state;
    // engines write streamType; isVod is always derived, never trusted from callers
    this.state = { ...prev, ...partial, isVod: this.derive({ ...prev, ...partial }) };

    for (const sub of this.subs) {
      const oldVal = sub.selector(prev);
      const newVal = sub.selector(this.state);
      if (oldVal !== newVal) {
        sub.listener(newVal, oldVal);
      }
    }
  }

  private derive(s: Partial<SettablePlayerState> & { isVod?: boolean }): boolean {
    return deriveIsVod(s.streamType ?? 'unknown');
  }

  subscribe<T>(selector: Selector<T>, listener: Listener<T>): () => void {
    const sub: Subscription = {
      selector: selector as Selector<unknown>,
      listener: listener as Listener<unknown>,
    };
    this.subs.push(sub);
    return () => {
      const idx = this.subs.indexOf(sub);
      if (idx !== -1) this.subs.splice(idx, 1);
    };
  }

  destroy(): void {
    this.subs.length = 0;
  }
}
